import { ChangeEvent, Dispatch as DispatchReact, SetStateAction, useCallback, useEffect, useState } from 'react';
import { connect, useDispatch } from 'react-redux';
import type { Dispatch } from 'redux';

import { Button } from 'UIPackage/MainModule/components/Button';
import { SearchInput } from 'UIPackage/MainModule/components/SearchInput';
import { Tooltip } from 'UIPackage/MainModule/components/Tooltip';
import { ISimilaritiesMatchByPersonFilter } from 'APIPackage/SimilarityModule/interfaces/ISimilaritiesMatchByPersonFilter';
import { RootActions } from 'CorePackage/MainModule/types/RootActions';
import { getSimilaritiesFilterById } from 'SimilaritiesPackage/MainModule/selectors/similaritiesSelectors';
import {
  similaritiesGetDataById,
  similaritiesSetFiltersById,
} from 'SimilaritiesPackage/MainModule/actions/similaritiesActions';
import { IRootState } from 'CorePackage/MainModule/interfaces/IRootState';
import { TableCheckItems } from 'CorePackage/MainModule/types/TableCheckItems';

import styles from './SimilaritiesByPersonPanel.module.scss';
import { SimilaritiesByPersonDropdown } from './SimilaritiesByPersonDropdown';

interface ISimilaritiesPanelProps {
  setFiltersModalVisible: DispatchReact<SetStateAction<boolean>>;
  itemsCheck: TableCheckItems;
  setSimilaritiesDateModalVisible: DispatchReact<SetStateAction<boolean>>;
  setActiveIds: DispatchReact<SetStateAction<string[] | number[]>>;
  activeIds: string[] | number[];
  filter: ISimilaritiesMatchByPersonFilter;
}

const SimilaritiesByPersonPanelWithoutStore = ({
  setFiltersModalVisible,
  itemsCheck,
  setSimilaritiesDateModalVisible,
  setActiveIds,
  activeIds,
  filter,
}: ISimilaritiesPanelProps) => {
  const dispatch = useDispatch<Dispatch<RootActions>>();

  const [inputError, setInputError] = useState('');
  const [searchString, setSearchString] = useState('');

  useEffect(() => {
    const active = Object.entries(itemsCheck)
      .filter(x => x[1])
      .map(x => Number(x[0]));

    setActiveIds(active);
  }, [itemsCheck, setActiveIds]);

  const onMergeClick = useCallback(() => {
    setSimilaritiesDateModalVisible(true);
  }, [setSimilaritiesDateModalVisible]);

  const onOpenFilterModal = useCallback(() => setFiltersModalVisible(true), [setFiltersModalVisible]);

  const handleBlur = useCallback((e: ChangeEvent<HTMLInputElement>) => {
    const { length } = e.target.value;

    if (length < 3 && length > 0) {
      setInputError('Длина строки должна быть не менее 3 символов');
    } else if (length > 1024) {
      setInputError('Превышена допустимая длина строки');
    } else {
      setInputError('');
    }
  }, []);

  const handleSearchInputSubmit = useCallback(() => {
    const filtersMap = { ...filter, searchString };

    dispatch(similaritiesSetFiltersById(filtersMap));
    dispatch(similaritiesGetDataById());
  }, [dispatch, filter, searchString]);

  const handleSearchInputChange = useCallback((e: { target: { value: SetStateAction<string> } }) => {
    setSearchString(e.target.value);
  }, []);

  return (
    <div className="shared-panel-container">
      <div className={styles.Actions}>
        <SearchInput
          className={styles.SearchInput}
          name="searchString"
          placeholder="Искать"
          error={inputError}
          onBlur={handleBlur}
          onSubmit={handleSearchInputSubmit}
          onChange={handleSearchInputChange}
        />
        <Tooltip
          placement="right"
          content={
            <div className={styles.TooltipContent}>
              Доступен поиск КНМ по следующим критериям:
              <ul>
                <li>Номер КНМ</li>
                <li>Номер плана</li>
                <li>Наименование КНО</li>
              </ul>
            </div>
          }
        >
          <span className={styles.TooltipIcon} />
        </Tooltip>
      </div>
      <div className={styles.Filters}>
        <div className={styles.Wrapper}>
          <Button text="Фильтры" color="primary" onClick={onOpenFilterModal} />
          <SimilaritiesByPersonDropdown activeIds={activeIds} onMergeClick={onMergeClick} />
        </div>
      </div>
    </div>
  );
};

const mapStateToProps = (state: IRootState) => ({
  filter: getSimilaritiesFilterById(state),
});

export const SimilaritiesByPersonPanel = connect(mapStateToProps)(SimilaritiesByPersonPanelWithoutStore);
